import React, { useState } from "react";
import { toast } from "react-toastify";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

// YouTube embed support for the LMS. (YouTube-only for now; Vimeo deliberately
// unsupported.)
//
// SECURITY MODEL: we never put a user-supplied URL straight into an iframe
// `src`. parseVideoEmbedUrl() extracts the provider + a strictly-validated video
// id (+ start time), and buildVideoEmbedSrc() reconstructs the embed URL from a
// fixed template. So the only thing a learner page ever loads is
// `youtube-nocookie.com/embed/<id>` — arbitrary third-party iframes can't be
// injected through this block. The backend re-derives + re-validates the same
// fields on save (see views/learning_section.py) so a tampered payload can't
// persist a bad embed.

const YOUTUBE_HOSTS = [
  "youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtube-nocookie.com",
  "youtu.be",
];
const YOUTUBE_PATH_PREFIXES = ["embed", "shorts", "v", "live"];
const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;

// Parse "t"/"start"/#t fragment into integer seconds. Accepts "90", "1m30s",
// "1h2m3s". Returns undefined when absent/unparseable.
function parseStartSeconds(url) {
  let val =
    url.searchParams.get("t") ||
    url.searchParams.get("start") ||
    null;
  if (!val && url.hash) {
    const m = url.hash.match(/t=([0-9hms]+)/i);
    if (m) val = m[1];
  }
  if (!val) return undefined;
  // Clamp to a sane 0..24h, matching the backend sanitizers.
  const clamp = (n) => (n > 0 && n <= 86400 ? n : n > 86400 ? 86400 : undefined);
  if (/^\d+$/.test(val)) {
    return clamp(parseInt(val, 10));
  }
  const m = val.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);
  if (m && (m[1] || m[2] || m[3])) {
    const total =
      parseInt(m[1] || 0, 10) * 3600 +
      parseInt(m[2] || 0, 10) * 60 +
      parseInt(m[3] || 0, 10);
    return clamp(total);
  }
  return undefined;
}

// Returns { provider, videoId, hash?, startSeconds? } or null if the string is
// not a recognized YouTube / Vimeo URL.
export function parseVideoEmbedUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  let url;
  try {
    // Prepend a scheme so bare hosts ("youtu.be/abc") still parse.
    url = new URL(
      /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    );
  } catch (e) {
    return null;
  }

  const host = url.hostname.replace(/^www\./i, "").toLowerCase();
  const startSeconds = parseStartSeconds(url);

  if (YOUTUBE_HOSTS.includes(host)) {
    let id = null;
    if (host === "youtu.be") {
      id = url.pathname.split("/").filter(Boolean)[0] || null;
    } else if (url.pathname === "/watch") {
      id = url.searchParams.get("v");
    } else {
      const parts = url.pathname.split("/").filter(Boolean);
      if (YOUTUBE_PATH_PREFIXES.includes(parts[0])) {
        id = parts[1] || null;
      }
    }
    if (id && YOUTUBE_ID_RE.test(id)) {
      return { provider: "youtube", videoId: id, startSeconds };
    }
    return null;
  }

  // Vimeo is intentionally not supported (embeds are YouTube-only for now).
  return null;
}

// Reconstruct the iframe src from validated parts (never from the raw URL).
// opts: { autoplay, jsApi, muted, loop, controls, hideAnnotations } for the
// swipe-deck player; section embeds call it with no opts.
export function buildVideoEmbedSrc(embed, opts = {}) {
  if (!embed || !embed.provider || !embed.videoId) return null;
  const { provider, videoId, startSeconds } = embed;
  const {
    autoplay = false,
    jsApi = false,
    muted = false,
    loop = false,
    controls = true,
    origin = null,
    // YouTube only: hide creator annotation cards (iv_load_policy=3).
    hideAnnotations = false,
  } = opts;

  if (provider === "youtube") {
    const params = new URLSearchParams({
      rel: "0",
      modestbranding: "1",
      playsinline: "1",
    });
    if (autoplay) params.set("autoplay", "1");
    if (muted) params.set("mute", "1");
    if (jsApi) {
      params.set("enablejsapi", "1");
      // YouTube recommends an explicit origin when the JS API is enabled.
      if (origin) params.set("origin", origin);
    }
    if (!controls) params.set("controls", "0");
    if (hideAnnotations) params.set("iv_load_policy", "3");
    if (loop) {
      params.set("loop", "1");
      params.set("playlist", videoId);
    }
    if (startSeconds) params.set("start", String(startSeconds));
    return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(
      videoId
    )}?${params.toString()}`;
  }

  return null;
}

const PROVIDER_LABEL = { youtube: "YouTube" };

// Block render component for the section editor + viewer. `mode === "edit"`
// shows the paste-a-URL affordance; any other mode renders read-only.
export const VideoEmbed = (props) => {
  const { block, editor, mode } = props;
  const isEdit = mode === "edit";
  const { provider, videoId, hash, startSeconds, title } = block.props;
  const [inputUrl, setInputUrl] = useState("");

  const hasVideo = Boolean(provider && videoId);

  const applyUrl = () => {
    const parsed = parseVideoEmbedUrl(inputUrl);
    if (!parsed) {
      toast.error("Enter a valid YouTube link");
      return;
    }
    editor.updateBlock(block, {
      type: "videoEmbed",
      props: {
        provider: parsed.provider,
        videoId: parsed.videoId,
        hash: parsed.hash || "",
        startSeconds: parsed.startSeconds || 0,
        title: "",
      },
    });
    setInputUrl("");
  };

  const clearVideo = () => {
    editor.updateBlock(block, {
      type: "videoEmbed",
      props: { provider: "", videoId: "", hash: "", startSeconds: 0, title: "" },
    });
  };

  // EDIT MODE, no video yet — paste-URL panel. Solid card with a header bar, so
  // it matches the populated embed (and the PDF / media blocks) instead of a
  // loud dashed placeholder.
  if (isEdit && !hasVideo) {
    return (
      <div
        className="my-3 w-full border border-gray-200 rounded-lg overflow-hidden bg-white outline-none focus:outline-none"
        contentEditable={false}
      >
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200">
          <OndemandVideoIcon className="text-violet-700" style={{ fontSize: 18 }} />
          <span className="text-xs font-medium text-gray-600">
            Embed a YouTube video
          </span>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyUrl();
                }
              }}
              placeholder="Paste a YouTube link"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <button
              type="button"
              onClick={applyUrl}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-700 hover:bg-violet-800 rounded-lg transition-colors"
            >
              Embed
            </button>
          </div>
          {/* div, not p — BlockNote's editor CSS styles <p> inside block
              content (inflates font-size, resets margin), so a paragraph here
              renders oversized and flush against the input. */}
          <div className="mt-2 text-xs text-gray-500">
            Supported: youtube.com, youtu.be
          </div>
        </div>
      </div>
    );
  }

  if (!hasVideo) {
    // Read-only with nothing configured — render nothing.
    return <div contentEditable={false} />;
  }

  const src = buildVideoEmbedSrc({ provider, videoId, hash, startSeconds });

  return (
    <div
      className="my-3 w-full border border-gray-200 rounded-lg overflow-hidden bg-white outline-none focus:outline-none"
      contentEditable={false}
    >
      {isEdit && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <OndemandVideoIcon className="text-violet-700" style={{ fontSize: 18 }} />
            <span className="text-xs font-medium text-gray-600">
              {PROVIDER_LABEL[provider] || "Video"} embed
            </span>
          </div>
          <button
            type="button"
            onClick={clearVideo}
            className="flex items-center gap-1 text-xs text-violet-700 hover:text-violet-800 font-medium transition-colors"
          >
            <SwapHorizIcon style={{ fontSize: 16 }} />
            Replace
          </button>
        </div>
      )}
      {/* 16:9 responsive frame. Reconstructed src only (see security note). */}
      <div style={{ position: "relative", width: "100%", paddingBottom: "56.25%" }}>
        <iframe
          src={src}
          title={title || `${PROVIDER_LABEL[provider] || "Video"} player`}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: "none",
          }}
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    </div>
  );
};
